// src/providers/dataProvider.ts
import { DataProvider } from "@refinedev/core";
import { collection, getDocs, doc, getDoc, query, where, orderBy, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { getCurrentUserToken } from "./authProvider";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    if (resource === 'users') {
      // Admin-only resource
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const users = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        return { data: users, total: users.length };
      } catch (error) {
        console.error("Error fetching users:", error);
        return { data: [], total: 0 };
      }
    }

    // Handle API-based resources
    if (resource === 'videos' || resource === 'scripts') {
      try {
        const token = await getCurrentUserToken();
        
        if (!token) {
          console.error("No authentication token available");
          return { data: [], total: 0 };
        }

        const impersonateUid = localStorage.getItem('impersonateUid');
        let url = `${API_URL}/user-jobs`;
        if (impersonateUid) {
          url += `?impersonate=${impersonateUid}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const jobs = result.jobs || [];

        // Filter jobs based on resource type
        let filteredData = jobs;
        if (resource === "videos") {
          filteredData = jobs.filter((job: any) => 
            job.generation_type !== 'script_only' && 
            job.tasks?.create_video?.status === 'COMPLETED'
          );
        } else if (resource === "scripts") {
          filteredData = jobs.filter((job: any) => 
            job.generation_type === 'script_only' || 
            job.tasks?.create_script?.status === 'COMPLETED'
          );
        }

        // Transform data for the list view
        const transformedData = filteredData.map((job: any) => ({
          id: job.job_id,
          title: job.original_filename?.replace('.pdf', '') || 'Untitled',
          status: job.status,
          createdAt: job.created_at,
          completedAt: job.completed_at,
          videoUrl: job.tasks?.create_video?.video_url,
          scriptText: job.tasks?.create_script?.script_text,
          generationType: job.generation_type,
          tasks: job.tasks,
        }));

        return {
          data: transformedData,
          total: transformedData.length,
        };
      } catch (error) {
        console.error("Error fetching list:", error);
        return { data: [], total: 0 };
      }
    }

    // Default Firestore handling for other resources
    return {
      data: [],
      total: 0,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    // Handle API-based resources
    if (resource === 'videos' || resource === 'scripts') {
      try {
        const token = await getCurrentUserToken();
        
        if (!token) {
          throw new Error("No authentication token available");
        }

        const impersonateUid = localStorage.getItem('impersonateUid');
        let url = `${API_URL}/user-jobs`;
        if (impersonateUid) {
          url += `?impersonate=${impersonateUid}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const job = result.jobs?.find((j: any) => j.job_id === id);

        if (!job) {
          throw new Error("Job not found");
        }

        return {
          data: {
            id: job.job_id,
            title: job.original_filename?.replace('.pdf', '') || 'Untitled',
            status: job.status,
            createdAt: job.created_at,
            completedAt: job.completed_at,
            videoUrl: job.tasks?.create_video?.video_url,
            scriptText: job.tasks?.create_script?.script_text,
            generationType: job.generation_type,
            tasks: job.tasks,
          },
        };
      } catch (error) {
        console.error("Error fetching one:", error);
        throw error;
      }
    }

    // Default Firestore handling
    const docRef = doc(db, resource, id as string);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        data: { id: docSnap.id, ...docSnap.data() } as any,
      };
    } else {
      throw new Error("Document not found");
    }
  },

  create: async ({ resource, variables, meta }) => {
    // Cast variables to unknown first, then to TData to satisfy TypeScript
    return { data: variables as any };
  },

  update: async ({ resource, id, variables, meta }) => {
    try {
      const docRef = doc(db, resource, id as string);
      await updateDoc(docRef, variables as { [key: string]: any });
      return { data: { id, ...variables } as any };
    } catch (error) {
      console.error("Error updating document:", error);
      throw error;
    }
  },
  
  deleteOne: async ({ resource, id, variables, meta }) => {
    try {
      const docRef = doc(db, resource, id as string);
      await deleteDoc(docRef);
      return { data: { id } as any };
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  },

  getApiUrl: () => {
    return API_URL;
  },

  custom: async ({ url, method, filters, sorters, payload, query, headers }) => {
    try {
      const token = await getCurrentUserToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Transform camelCase to snake_case for payload
      const transformedPayload = payload ? transformToSnakeCase(payload) : undefined;

      const response = await fetch(`${API_URL}${url}`, {
        method: method || "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          ...headers,
        },
        body: transformedPayload ? JSON.stringify(transformedPayload) : undefined,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Transform snake_case to camelCase for response
      const transformedData = transformToCamelCase(responseData);

      return { data: transformedData };
    } catch (error) {
      console.error("Custom request error:", error);
      throw error;
    }
  },
};

// Helper functions for case transformation
function transformToSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformToSnakeCase);
  if (typeof obj !== 'object') return obj;

  const result: any = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    result[snakeKey] = transformToSnakeCase(obj[key]);
  }
  return result;
}

function transformToCamelCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformToCamelCase);
  if (typeof obj !== 'object') return obj;

  const result: any = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = transformToCamelCase(obj[key]);
  }
  return result;
}